import React, { useMemo, useState } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import fluxQuerys from "./AnalyticsFluxQuery";
import helpers from "../AnalyticsHelpers";

import AnalyticsSearch from "./AnalyticsSearch";
import AnalyticsResults from "../analyticsResults/AnalyticsResults";
import AnalyticsComparisonChart from "../analyticsResults/AnalyticsComparisonChart";
import Loading from "../Loading";

function AnalyticsCustomSearchProject({
  dispatch,
  projectId,
  apartments,
  projectTimeZone,
  metricsConfig,
  darkMode,
  t,
}) {
  const aptOptions = useMemo(() => {
    const apartList = helpers.getApartList(apartments);
    const allApartmentsOption = {
      label: t("allApartments"),
      value: "ALL_APARTMENS",
      apartmentId: "ALL_APARTMENS",
    };

    return [allApartmentsOption, ...apartList];
  }, [apartments]);

  const [charts, setCharts] = useState([]);
  const [comparisonChartIds, setComparisonChartIds] = useState([]);
  const [loading, setLoading] = useState(false);

  // const comparisonCandidate = useMemo(() => {
  //   const grouped = charts.reduce((acc, chart) => {
  //     if (chart?.allApartments) return acc;
  //     if (!chart?.apartmentId || !chart?.queryPeriod) return acc;
  //     if (!Array.isArray(chart?.points) || chart.points.length === 0) return acc;

  //     const key = chart.queryPeriod;
  //     if (!acc[key]) acc[key] = [];
  //     acc[key].push(chart);
  //     return acc;
  //   }, {});

  //   const firstPairGroup = Object.values(grouped).find((group) => group.length >= 2);
  //   if (!firstPairGroup) return null;

  //   const [firstChart, secondChart] = firstPairGroup;
  //   return {
  //     chartIds: [firstChart.id, secondChart.id],
  //     charts: [firstChart, secondChart],
  //     queryPeriod: firstChart.queryPeriod,
  //   };
  // }, [charts]);

  const activeComparisonCharts = useMemo(() => {
    if (!comparisonChartIds.length) return [];
    const selected = charts.filter((chart) => comparisonChartIds.includes(chart?.id));
    if (selected.length !== 2) return [];
    return selected;
  }, [charts, comparisonChartIds]);

  // const handleCompare = () => {
  //   if (!comparisonCandidate) {
  //     setComparisonChartIds([]);
  //     return;
  //   }

  //   setComparisonChartIds(comparisonCandidate.chartIds);
  // };

  const handleSearchSubmit = (payload) => {
    setLoading(true);

    const {
      allApartments,
      deviceId,
      apartmentId,
      apartmentLabel,
      measurement,
      period,
      field,
      referenceDate,
      mode,
      startDate,
      endDate,
    } = payload;

    const measurementLabel = helpers.getMetricTitleLabel({
      metricKey: measurement,
      metricsConfig,
      t,
    });
    const tz = projectTimeZone || "UTC";

    // Calcular startISO/stopISO solo para byDates
    let startISO = "";
    let stopISO = "";
    if (mode === "byDates") {
      const range = helpers.getStartAndStopISOByDates(startDate, endDate);
      startISO = range?.startISO || "";
      stopISO = range?.stopISO || "";

      if (!startISO || !stopISO) {
        setLoading(false);
        return;
      }
    }

    // ALLAPARTMENTS

    if (allApartments) {
      const allApartmentsOptions = aptOptions.filter((opt) => opt?.value !== "ALL_APARTMENS");

      if (allApartmentsOptions.length === 0) {
        setLoading(false);
        return;
      }

      // ALLAPARTMENTS -BYDATES (daily/monthly)
      if (mode === "byDates") {
        const title = helpers.buildSingleTitle({
          apartmentLabel: t("allApartments"),
          measurementLabel,
          period,
          timeZone: tz,
          t,
          mode,
          startDate,
          endDate,
        });
        const allAparmentsPoints = [];
        let done = 0;

        allApartmentsOptions.forEach((apartment) => {
          const fx = fluxQuerys.proyectByDatesFlux({
            bucket: `onna-${apartment?.onnaId}`,
            type: period,
            measurement,
            apartmentId: apartment?.apartmentId,
            field,
            startISO,
            stopISO,
          });

          dispatch({
            type: "MAKE_QUERY",
            payload: {
              source: "cloud",
              projectId,
              deviceIds: [apartment?.onnaId],
              flux: fx,
              cb: (res) => {
                const rows = res?.results?.[0]?.rows || [];
                const mappedRows = helpers.rowsToPoints(rows, period, tz, t);
                mappedRows.forEach((rowPoint) => {
                  allAparmentsPoints.push({
                    label: rowPoint?.label,
                    value: rowPoint?.value,
                    state: rowPoint?.state ?? "",
                    apartment: apartment?.label,
                    timestamp: rowPoint?.timestamp,
                  });
                });

                done += 1;
                if (done === allApartmentsOptions.length) {
                  const chartId = `all-${measurement}-${period}-byDates-${field}-${Date.now()}`;
                  setCharts((prev) => [
                    {
                      id: chartId,
                      title,
                      referenceDate: startISO,
                      allApartments: true,
                      apartmentLabel: t("allApartments"),
                      queryPeriod: period,
                      measurement,
                      field,
                      points: helpers.sortPointsByTimestampAndApartment(allAparmentsPoints),
                    },
                    ...prev,
                  ]);
                  setLoading(false);
                }
              },
            },
          });
        });

        return;
      }

      // ALLAPARTMENTS - STANDARD (hourly/daily/monthly)

      const title = helpers.buildAllTitle({
        measurementLabel,
        period,
        referenceDate,
        timeZone: tz,
        t,
      });

      const allAparmentsPoints = [];
      let allApartmentsOptionsDone = 0;

      allApartmentsOptions.forEach((apartment) => {
        const fx = fluxQuerys.proyectAllApartFlux({
          bucket: `onna-${apartment?.onnaId}`,
          tz,
          period,
          measurement,
          apartmentId: apartment?.apartmentId,
          field,
          referenceDateISO: referenceDate,
          t,
        });

        dispatch({
          type: "MAKE_QUERY",
          payload: {
            source: "cloud",
            projectId,
            deviceIds: [apartment?.onnaId],
            flux: fx,
            cb: (res) => {
              const rows = res?.results?.[0]?.rows || [];

              const mappedRows = helpers.rowsToPoints(rows, period, tz, t);

              mappedRows.forEach((rowPoint) => {
                allAparmentsPoints.push({
                  label: rowPoint?.label,
                  value: rowPoint?.value,
                  state: rowPoint?.state ?? "",
                  apartment: apartment?.label,
                  timestamp: rowPoint?.timestamp,
                });
              });

              allApartmentsOptionsDone += 1;

              if (allApartmentsOptionsDone === allApartmentsOptions.length) {
                const chartId = `all-${measurement}-${period}-${field}-${Date.now()}`;

                setCharts((prev) => [
                  {
                    id: chartId,
                    title,
                    referenceDate,
                    allApartments: true,
                    apartmentLabel: t("allApartments"),
                    queryPeriod: period,
                    measurement,
                    field,
                    points: helpers.sortPointsByTimestampAndApartment(allAparmentsPoints),
                  },
                  ...prev,
                ]);
                setLoading(false);
              }
            },
          },
        });
      });

      return;
    }

    // UN APARTAMENTO
    const chartId = `${deviceId}-${apartmentId}-${measurement}-${period}-${field}-${Date.now()}`;

    // UN APART - BYDATES (daily/monthly)
    if (mode === "byDates") {
      const fx = fluxQuerys.proyectByDatesFlux({
        bucket: `onna-${deviceId}`,
        type: period,
        measurement,
        apartmentId,
        field,
        startISO,
        stopISO,
      });

      dispatch({
        type: "MAKE_QUERY",
        payload: {
          source: "cloud",
          projectId,
          deviceIds: [deviceId],
          flux: fx,
          cb: (resp) => {
            const rows = resp?.results?.[0]?.rows || [];
            const points = helpers.rowsToPoints(rows, period, tz, t, true);
            const title = helpers.buildSingleTitle({
              apartmentLabel,
              measurementLabel,
              period,
              timeZone: tz,
              t,
              mode,
              startDate,
              endDate,
            });

            setCharts((prev) => [
              {
                id: chartId,
                title,
                referenceDate: startISO,
                points,
                allApartments: false,
                apartmentId,
                apartmentLabel,
                measurement,
                field,
                queryPeriod: period,
              },
              ...prev,
            ]);
            setLoading(false);
          },
        },
      });

      return;
    }

    // UN APART - STANDARD (hourly/daily/monthly)
    const fx = fluxQuerys.proyectSingleApartmentFlux({
      bucket: `onna-${deviceId}`,
      tz,
      period,
      measurement,
      apartmentId,
      field,
      referenceDateISO: referenceDate,
    });

    dispatch({
      type: "MAKE_QUERY",
      payload: {
        source: "cloud",
        projectId,
        deviceIds: [deviceId],
        flux: fx,
        cb: (resp) => {
          const rows = resp?.results?.[0]?.rows || [];

          const points = helpers.rowsToPoints(rows, period, tz, t);

          const title = helpers.buildSingleTitle({
            apartmentLabel,
            measurementLabel,
            period,
            referenceDate,
            timeZone: tz,
            t,
            mode,
            startDate,
            endDate,
          });

          setCharts((prev) => [
            {
              id: chartId,
              title,
              points,
              allApartments: false,
              apartmentId,
              apartmentLabel,
              measurement,
              field,
              queryPeriod: period,
            },
            ...prev,
          ]);
          setLoading(false);
        },
      },
    });
  };

  return (
    <MDBox style={{ padding: 16 }}>
      <AnalyticsSearch
        apartmentOptions={aptOptions}
        onSubmit={handleSearchSubmit}
        metricsConfig={metricsConfig}
        loading={loading}
        // showCompareControls={!!comparisonCandidate}
        // compareSummaryText=""
        // compareDisabled={!comparisonCandidate}
        // onCompare={handleCompare}
        darkMode={darkMode}
        t={t}
      />

      {activeComparisonCharts.length === 2 && (
        <AnalyticsComparisonChart
          charts={activeComparisonCharts}
          darkMode={darkMode}
          metricsConfig={metricsConfig}
          projectTimeZone={projectTimeZone}
          queryPeriod={activeComparisonCharts?.[0]?.queryPeriod}
          t={t}
        />
      )}

      <MDBox style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {loading && <Loading darkMode={darkMode} t={t} />}
        {charts.map((chart) => (
          <AnalyticsResults
            key={chart?.id}
            title={chart?.title}
            points={chart?.points}
            queryPeriod={chart?.queryPeriod}
            allApartments={chart?.allApartments}
            projectTimeZone={projectTimeZone}
            measurement={chart?.measurement}
            field={chart?.field}
            metricsConfig={metricsConfig}
            darkMode={darkMode}
            onRemove={() => {
              if (comparisonChartIds.includes(chart?.id)) {
                setComparisonChartIds([]);
              }
              helpers.removeChart(chart?.id, setCharts);
            }}
            t={t}
          />
        ))}
      </MDBox>
    </MDBox>
  );
}

AnalyticsCustomSearchProject.propTypes = {
  dispatch: PropTypes.func.isRequired,
  projectId: PropTypes.string,
  apartments: PropTypes.object.isRequired,
  projectTimeZone: PropTypes.string,
  metricsConfig: PropTypes.object,
  darkMode: PropTypes.bool,
  t: PropTypes.func,
};

AnalyticsCustomSearchProject.defaultProps = {
  projectId: "",
  projectTimeZone: "UTC",
  metricsConfig: {},
  darkMode: false,
  t: () => {},
};

export default connect((state, ownProps) => ({
  projectId: ownProps.projectId,
  apartments: ownProps.apartments,
  projectTimeZone: ownProps.projectTimeZone,
  metricsConfig: ownProps.metricsConfig,
  darkMode: ownProps.darkMode,
  t: ownProps.t,
}))(AnalyticsCustomSearchProject);
